import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { ChapterLabel } from "@/components/Expertise";
import { useLanguage } from "@/i18n/LanguageContext";
import { testimonialsData } from "@/i18n/testimonialsData";

const PJ_URL = "https://www.pagesjaunes.fr/pros/52603125#blocAvis";

function Stars({ size = 14, testid }) {
  return (
    <span className="flex gap-1" data-testid={testid}>
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={size} className="text-brass" fill="currentColor" strokeWidth={0} />
      ))}
    </span>
  );
}

export default function Testimonials() {
  const { lang } = useLanguage();
  const d = testimonialsData[lang] || testimonialsData.fr;

  return (
    <section id="temoignages" data-testid="testimonials-section" className="bg-paper py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <ChapterLabel num="06" title={d.label} />
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink"
          >
            {d.title}
          </motion.h2>
          <motion.a
            href={PJ_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="testimonials-rating"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="group flex items-center gap-5 w-fit"
          >
            <span className="font-serif font-light text-5xl lg:text-6xl text-ink leading-none">5<span className="text-brass">/5</span></span>
            <span>
              <Stars testid="testimonials-stars" />
              <span className="mt-2.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink/50 group-hover:text-brass transition-colors duration-300">
                {d.ratingLabel} <ExternalLink size={11} />
              </span>
            </span>
          </motion.a>
        </div>
        <div className="grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10 max-w-5xl">
          {d.items.map((item, i) => (
            <motion.blockquote
              key={i}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="bg-paper p-10 lg:p-12 flex flex-col hover:bg-[#F1EFE9] transition-colors duration-500"
            >
              <Stars size={13} testid={`testimonial-stars-${i}`} />
              <p className="font-serif text-xl lg:text-2xl font-light leading-snug text-ink flex-1 mt-7">{item.quote}</p>
              <footer className="mt-10 border-t border-ink/10 pt-5">
                <p className="text-sm text-ink font-medium">{item.name}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-ink/45 mt-1.5">{item.meta}</p>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
